import React, { useState } from "react";
import { BasicUserType } from "@/types/user";
import { PostType } from "@/types/post";
// @ts-ignore - Prisma client types
import type {  User  } from '@prisma/client';
import { useRouter } from "next/navigation";
import { useToast } from "@/components/ui/use-toast";
import { createPost, quotePost } from "@/actions/posts/index";

import { PostHeader } from "./post/PostHeader";
import { PostContent } from "./post/PostContent";
import { PostActions } from "./post/PostActions";
import { PostFooter } from "./post/PostFooter";
import { DeleteModal, QuoteModal } from "./post/PostModals";
import { PostWrapper } from "./post/PostWrapper";
import { PostVoting } from "./post/PostVoting";
import { PostInteractions } from "./post/PostInteractions";
import { PostUserInfo } from "./post/PostUserInfo";
import { QuotedPost } from "./post/QuotedPost";

export default function PostComponent({
  id,
  name,
  username,
  image,
  content,
  createdAt,
  likes = [],
  reposts = [],
  replies = [],
  files = [],
  views = [],
  viewCount = 0,
  type = "post",
  currentUser,
  originalPostId = null,
  userId,
  medias = [],
  Post: originalPost = null,
  poll,
  locationName,
  meal,
  routeData,
  onRepost,
  onPostClick,
  onProfileClick,
  onProfileHover,
  user,
  user_username,
}: PostType & {
  currentUser?: BasicUserType | null;
  onRepost?: (repostedPost: any) => void;
  onPostClick?: (post: PostType) => void;
  onProfileClick?: (username: string, profile?: BasicUserType) => void;
  onProfileHover?: (username: string) => void;
}) {
  const [isDeleted, setIsDeleted] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
  const [quoteContent, setQuoteContent] = useState("");
  const [isAnyModalOpen, setIsAnyModalOpen] = useState(false);

  // Update modal state tracking
  React.useEffect(() => {
    setIsAnyModalOpen(isDeleteModalOpen || isQuoteModalOpen);
  }, [isDeleteModalOpen, isQuoteModalOpen]);

  const router = useRouter();
  const { toast } = useToast();

  // Debug meal data (disabled for production)
  // console.log("Post component received meal data:", {
  //   meal,
  //   type,
  //   hasMeal: !!meal,
  //   mealDetails: meal ? {
  //     id: meal.id,
  //     name: meal.name,
  //     nutritionalInfo: meal.nutritionalInfo,
  //     isOrganic: meal.isOrganic,
  //     hasSeedOils: meal.hasSeedOils,
  //     calories: meal.calories,
  //     protein: meal.protein,
  //     carbs: meal.carbs,
  //     fats: meal.fats
  //   } : null
  // });
  
  // If this is a repost, use the original post's data
  const postData = {
    id,
    name: user?.name || name,
    username: user?.username || user_username || username || 'user',
    image: user?.image || image,
    content,
    createdAt,
    updatedAt: createdAt,
    likes,
    reposts,
    replies,
    files,
    views,
    viewCount,
    userId,
    medias,
    poll,
    locationName,
    meal,
    routeData,
    originalPostId: null,
    type: (type || "post") as PostType["type"],
    bookmarks: [],
  };

  const postToShow = (type === "repost" && originalPost) ? originalPost : postData;

  // Safety check: if postToShow is invalid, don't render
  if (!postToShow || (!postToShow.id && !id)) {
    console.warn('Post component received invalid post data:', { postToShow, id });
    return null;
  }

  const {
    hasVoted,
    pollResults,
    totalVotes,
    handleVote,
  } = PostVoting({
    poll,
    currentUser,
    onVoteStatusChange: (hasVoted, results, totalVotes) => {
      // Handle vote status changes if needed
    },
  });

  const {
    isLiked,
    likeCount,
    isReposted,
    repostCount,
    isBookmarked,
    bookmarkCount,
    handleLike,
    handleRepost,
    handleBookmark,
  } = PostInteractions({
    id,
    userId,
    currentUser,
    initialLikes: likes,
    initialReposts: reposts,
    onInteractionChange: (type, count, isActive) => {
      // Handle interaction changes if needed
    },
    onRepost,
  });

  const {
    targetUser,
    isFollowing,
    handleFollowToggle,
  } = PostUserInfo({
    id,
    userId,
    username,
    currentUser,
    onUserInfoChange: (user, following) => {
      // Handle user info changes if needed
    },
  });

  const handleQuoteModalClose = () => {
    setIsQuoteModalOpen(false);
    setQuoteContent("");
  };

  const handleDelete = () => {
    setIsDeleted(true);
    setIsDeleteModalOpen(false);
  };

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onProfileClick) {
      onProfileClick(postToShow.username);
    } else {
      router.push(`/${postToShow.username}`);
    }
  };

  const handleAvatarHover = () => {
    if (onProfileHover) {
      onProfileHover(postToShow.username);
    }
  };

  return (
    <>
      <PostWrapper
        post={postToShow}
        isDeleted={isDeleted}
        onAvatarClick={handleAvatarClick}
        onPostClick={onPostClick}
        showRepostHeader={type === "repost"}
        repostUserName={name}
      >
        <PostHeader
          name={postToShow.name}
          username={postToShow.username}
          image={postToShow.image}
          createdAt={postToShow.createdAt}
          currentUserId={currentUser?.id}
          userId={postToShow.userId}
          isFollowing={isFollowing}
          targetUser={targetUser}
          onFollowToggle={handleFollowToggle}
          onDelete={() => setIsDeleteModalOpen(true)}
          onProfileClick={onProfileClick}
        />

        {type === "quote" ? (
          <>
            <p className="mt-5 mb-2 whitespace-pre-wrap">{content}</p>
            {console.log('🔍 Quote post frontend debug:', {
              type,
              originalPost: originalPost ? { id: originalPost.id, content: originalPost.content?.substring(0, 50) + '...' } : null,
              originalPostId,
              hasOriginalPost: !!originalPost
            })}
            <QuotedPost
              post={originalPost}
              originalPostId={originalPostId}
              onPostClick={onPostClick}
            />
          </>
        ) : (
          <PostContent
            content={postToShow.content}
            type={postToShow.type || "post"}
            poll={postToShow.poll}
            hasVoted={hasVoted}
            pollResults={pollResults}
            totalVotes={totalVotes}
            onVote={handleVote}
            medias={postToShow.medias}
            postId={postToShow.id}
            meal={postToShow.meal}
            locationName={postToShow.locationName}
            routeData={postToShow.routeData}
          />
        )}
<PostActions
  postId={postToShow.id?.toString() || id?.toString() || ''}
  replyCount={replies.length}
  likeCount={likeCount}
  repostCount={repostCount}
  bookmarkCount={bookmarkCount}
  viewCount={postToShow.viewCount}
  isLiked={isLiked}
  isReposted={isReposted}
  isBookmarked={isBookmarked}
  onLike={handleLike}
  onRepost={handleRepost}
  onQuote={() => setIsQuoteModalOpen(true)}
  onBookmark={handleBookmark}
  isAnyModalOpen={isAnyModalOpen}
          onCopyLink={() => {
            navigator?.clipboard?.writeText(`${window?.location?.origin}/${user?.username || user_username || username || 'user'}/post/${id}`);
            toast({
              title: "Success",
              description: "Link copied to clipboard",
            });
          }}
          onShare={() => {
            if (navigator.share) {
              navigator.share({
                title: `Post by ${name}`,
                text: content,
                url: `${window?.location?.origin}/${user?.username || user_username || username || 'user'}/status/${id}`,
              }).catch((error) => {
                console.error('Error sharing:', error);
                toast({
                  title: "Error",
                  description: "Failed to share post",
                  variant: "destructive",
                });
              });
            } else {
              navigator?.clipboard?.writeText(`${window?.location?.origin}/${user?.username || user_username || username || 'user'}/post/${id}`);
              toast({
                title: "Success",
                description: "Link copied to clipboard",
              });
            }
          }}
          onSendDM={() => {
            if (!currentUser) {
              toast({
                title: "Error",
                description: "Please log in to send messages",
                variant: "destructive",
              });
              return;
            }
            router.push(`/messages?share=${id}`);
          }}
        />

        <PostFooter
          type={type}
          createdAt={postToShow.createdAt}
          viewCount={postToShow.viewCount}
          locationName={postToShow.locationName}
        />
      </PostWrapper>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDelete}
      />

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={handleQuoteModalClose}
        onQuote={async () => {
          try {
            const result = await quotePost(id, quoteContent);

            if (result) {
              toast({
                title: "Success",
                description: "Post quoted successfully",
              });
              router.refresh();
            }
          } catch (error) {
            console.error('Error quoting post:', error);
            toast({
              title: "Error",
              description: "Failed to quote post",
              variant: "destructive",
            });
          }
          handleQuoteModalClose();
        }}
        name={name}
        username={username}
        content={content}
        quoteContent={quoteContent}
        onQuoteContentChange={setQuoteContent}
        originalPost={{
          id,
          name,
          username,
          image,
          content,
          createdAt,
          updatedAt: createdAt,
          userId,
          type: type || "post",
          likes: likes || [],
          reposts: reposts || [],
          files: files || [],
          medias: medias || [],
          views: views || [],
          viewCount: viewCount || 0,
          originalPostId: originalPostId || null,
          replies: replies || [],
          poll: poll || undefined,
          meal: meal || undefined,
          routeData: routeData || undefined,
          bookmarks: [],
        }}
      />
    </>
  );
}
